(ns hai.ui.components.date-picker
  "Framework-agnostic date / date-and-time picker. Pure reagent + Ornament, no
   re-frame and no Radix, so it runs in both the web-app and the (reagent-only)
   browser extension.

   Value is the local selection, timezone-free:

     {:date {:year 2026 :month 6 :day 8}   ; nil / absent = nothing picked
      :time 1020}                          ; minutes since midnight; nil = date-only

   The Date / Date & time toggle simply controls whether `:time` is present.
   Converting the selection to an absolute instant + timezone is the caller's
   job, kept out of here so the component stays reusable."
  {:ornament/prefix "datepicker-"}
  (:require
   [clojure.string :as str]
   [hai.ui.components.datetime :as datetime]
   [hai.ui.tokens :as t]
   [lambdaisland.ornament :as o]
   [reagent.core :as r]))

;; ---------------------------------------------------------------------------
;; Date helpers (calendar math via native js/Date in the browser's local zone)
;; ---------------------------------------------------------------------------

(defn- ->js-date [{:keys [year month day]}]
  (js/Date. year (dec month) (or day 1)))

(defn- js-date->parts [d]
  {:year (.getFullYear d) :month (inc (.getMonth d)) :day (.getDate d)})

(defn today-date
  "Today as a selection date map, for callers that default a picked time to it."
  []
  (js-date->parts (js/Date.)))

(defn- add-days [date n]
  (let [d (->js-date date)]
    (.setDate d (+ (.getDate d) n))
    (js-date->parts d)))

(defn- this-weekend-date
  "Upcoming Saturday, or today when today is already Saturday."
  []
  (let [dow (.getDay (->js-date (today-date)))]
    (add-days (today-date) (mod (- 6 dow) 7))))

(defn- shift-month [{:keys [year month]} delta]
  (let [idx (+ (* year 12) (dec month) delta)]
    {:year (quot idx 12) :month (inc (mod idx 12))}))

(defn- month-cells
  "Flat seq of cell maps for the month grid (week starts Monday): the month's
   days as `{:day n}`, padded to whole weeks with the previous/next month's
   days as `{:day n :adjacent? true}`."
  [{:keys [year month]}]
  (let [leading (mod (+ (.getDay (js/Date. year (dec month) 1)) 6) 7)
        days (.getDate (js/Date. year month 0))
        prev-days (.getDate (js/Date. year (dec month) 0))
        trailing (mod (- 7 (mod (+ leading days) 7)) 7)]
    (concat
     (for [d (range (inc (- prev-days leading)) (inc prev-days))]
       {:day d :adjacent? true})
     (for [d (range 1 (inc days))]
       {:day d})
     (for [d (range 1 (inc trailing))]
       {:day d :adjacent? true}))))

(defn- same-date? [a b]
  (and a b
       (= (:year a) (:year b))
       (= (:month a) (:month b))
       (= (:day a) (:day b))))

(defn- month-label [view]
  (.toLocaleDateString (->js-date (assoc view :day 1))
                       "en-US" #js {:month "long" :year "numeric"}))

;; ---------------------------------------------------------------------------
;; Time helpers
;; ---------------------------------------------------------------------------

(def ^:private default-time-minutes (* 9 60))

(defn format-minutes
  "Minutes since midnight as a 12-hour label, e.g. 1020 -> \"5:00 PM\"."
  [minutes]
  (let [h24 (quot minutes 60)
        mm (mod minutes 60)
        am-pm (if (< h24 12) "AM" "PM")
        h12 (let [h (mod h24 12)] (if (zero? h) 12 h))]
    (str h12 ":" (.padStart (str mm) 2 "0") " " am-pm)))

(defn- clock-parts
  "Minutes since midnight as the three things the clock boxes edit."
  [minutes]
  (let [h24 (quot minutes 60)]
    {:hour (let [h (mod h24 12)] (if (zero? h) 12 h))
     :minute (mod minutes 60)
     :pm? (>= h24 12)}))

(defn- clock-parts->minutes [{:keys [hour minute pm?]}]
  (+ (* 60 (+ (mod hour 12) (if pm? 12 0))) minute))

;; ---------------------------------------------------------------------------
;; Styles
;; ---------------------------------------------------------------------------

(o/defstyled frame :div
  {:display "flex" :flex-direction "column" :gap "12px"
   :color "#2D3E2A"
   :font-size "13px"
   :-webkit-tap-highlight-color "transparent"}

  [:.dp-toggle
   {:display "inline-flex" :align-self "flex-start"
    :gap "2px" :padding "2px"
    :background "#E8EADF" :border-radius "8px"}]
  [:.dp-toggle-opt
   {:padding "5px 12px"
    :border "none" :background "transparent" :border-radius "6px"
    :font-family "inherit" :font-size "12px" :font-weight "500"
    :color "#6B7560" :cursor "pointer"}
   [:&.active
    {:background "white" :color "#2D3E2A"
     :box-shadow "0 1px 2px rgba(45,62,42,0.12)"}]]

  [:.dp-chips
   {:display "flex" :flex-wrap "wrap" :gap "8px"}]
  [:.dp-chip
   {:padding "5px 12px"
    :border "1px solid #D9E0CE" :border-radius "999px"
    :background "transparent"
    :font-family "inherit" :font-size "12px" :font-weight "500"
    :color "#5B6F4E" :cursor "pointer"}
   [:&:hover {:border-color "#C2CDB1" :color "#2D3E2A"}]
   ;; Picked: the chips' hairline, not a fill.
   [:&.active
    {:background "transparent" :border-color t/--note-sage-55 :color t/--card-forest}]]

  [:.dp-cal-nav
   {:display "flex" :align-items "center" :justify-content "space-between"
    :margin-bottom "6px"}]
  [:.dp-cal-title
   {:font-size "14px" :font-weight "600"}]
  [:.dp-nav-btn
   {:width "28px" :height "28px"
    :display "inline-flex" :align-items "center" :justify-content "center"
    :border "none" :background "transparent" :border-radius "6px"
    :font-size "18px" :line-height "1" :color "#6B7560" :cursor "pointer"}
   [:&:hover {:background "#ECEFE4" :color "#2D3E2A"}]]
  [:.dp-weekdays
   {:display "grid" :grid-template-columns "repeat(7, 1fr)"
    :margin-bottom "4px"}]
  [:.dp-weekday
   {:text-align "center" :font-size "11px" :font-weight "500"
    :color "#9AA690"}]
  [:.dp-grid
   {:display "grid" :grid-template-columns "repeat(7, 1fr)" :gap "2px"}]
  ;; Every cell carries the outline so the picked one can colour it in without
  ;; the grid shifting under it.
  [:.dp-day
   {:aspect-ratio "1" :width "100%" :padding "0"
    :display "flex" :align-items "center" :justify-content "center"
    :box-sizing "border-box"
    :border-width "1px" :border-style "solid" :border-color "transparent"
    :background "transparent"
    :border-radius "8px" :font-family "inherit" :font-size "13px"
    :color "#2D3E2A" :cursor "pointer"}
   [:&:hover {:background "#ECEFE4"}]
   [:&.today {:box-shadow "inset 0 0 0 1.5px #A8BA93"}]
   ;; Outlined, not filled — the chips' language. Darker than today's ring, and
   ;; bold, so the two never trade places.
   [:&.selected
    {:border-color t/--note-sage :color t/--card-forest :font-weight "700"}]
   ;; Adjacent-month days pad the grid to whole weeks; visible but inert.
   [:&.adjacent
    {:color t/--card-sage-light :cursor "default" :pointer-events "none"}]]

  [:.dp-time-label
   {:font-size "11px" :font-weight "600" :letter-spacing "0.04em"
    :text-transform "uppercase" :color "#6B7560" :margin-bottom "6px"}]
  ;; The clock: two digit boxes and AM/PM, framed by the dashed ghost outline
  ;; the optional-extra pills use.
  [:.dp-time-box
   {:box-sizing "border-box"
    :display "flex" :align-items "center" :gap "10px"
    :padding "10px 12px"
    :border-width "1px" :border-style "dashed" :border-color "rgba(50,64,37,0.3)"
    :border-radius "12px"
    :background "transparent"
    :color "#6B7560"}
   [:svg {:width "16px" :height "16px" :flex "0 0 auto"}]]
  [:.dp-clock
   {:flex "1 1 auto"
    :display "flex" :align-items "center" :justify-content "center" :gap "6px"
    :font-size "17px" :font-weight "700" :letter-spacing "0.02em"
    :color "#2D3E2A"}]
  ;; White field chrome, same as the text inputs the composers use.
  [:.dp-clock-field
   {:width "46px" :box-sizing "border-box"
    :padding "5px 4px"
    :border-width "1px" :border-style "solid" :border-color t/--property-3
    :border-radius "8px"
    :background "#FFFFFF"
    :box-shadow "0 1px 2px rgba(45,62,42,0.05)"
    :font-family "inherit" :font-size "inherit" :font-weight "inherit"
    :color "inherit" :text-align "center"}
   [:&:focus
    {:outline "none" :border-color "#A8BA93"
     :box-shadow "0 0 0 3px rgba(156,174,133,0.18)"}]]
  ;; Dashed ghost when off, outlined chip when on — the extras' two states.
  [:.dp-ampm
   {:display "inline-flex" :gap "6px" :flex "0 0 auto"}]
  [:.dp-ampm-opt
   {:padding "5px 10px"
    :border-width "1px" :border-style "dashed" :border-color "rgba(50,64,37,0.3)"
    :border-radius "7px"
    :background "transparent"
    :font-family "inherit"
    :font-size "12px" :font-weight "600" :line-height "1"
    :color "rgba(74,93,63,0.82)"
    :cursor "pointer"
    :transition "background 0.15s, border-color 0.15s"}
   [:&:hover {:background "rgba(50,64,37,0.06)" :border-color "rgba(50,64,37,0.5)"}]
   [:&.active
    {:border-style "solid" :border-color t/--note-sage-55
     :color t/--card-forest}]])

;; ---------------------------------------------------------------------------
;; Sub-components
;; ---------------------------------------------------------------------------

(def ^:private weekday-initials ["MO" "TU" "WE" "TH" "FR" "SA" "SU"])

(defn- timed? [value] (some? (:time value)))

(defn- mode-toggle [value on-change]
  (let [datetime? (timed? value)]
    [:div.dp-toggle
     [:button.dp-toggle-opt
      {:type "button"
       :class (when-not datetime? "active")
       :on-click #(on-change (dissoc value :time))}
      "Date"]
     [:button.dp-toggle-opt
      {:type "button"
       :class (when datetime? "active")
       :on-click #(on-change (assoc value :time (or (:time value) default-time-minutes)))}
      "Date & time"]]))

(defn- quick-chips [value on-change !view]
  (let [selected (:date value)
        pick! (fn [date]
                (reset! !view (select-keys date [:year :month]))
                (on-change (assoc value :date date)))
        options [["Today" (today-date)]
                 ["Tomorrow" (add-days (today-date) 1)]
                 ["This weekend" (this-weekend-date)]]]
    [:div.dp-chips
     (for [[label date] options]
       ^{:key label}
       [:button.dp-chip
        {:type "button"
         :class (when (same-date? selected date) "active")
         :on-click #(pick! date)}
        label])]))

(defn- calendar [value on-change !view]
  (let [view @!view
        selected (:date value)
        today (today-date)]
    [:div.dp-cal
     [:div.dp-cal-nav
      [:button.dp-nav-btn
       {:type "button" :aria-label "Previous month"
        :on-click #(swap! !view shift-month -1)}
       "‹"]
      [:div.dp-cal-title (month-label view)]
      [:button.dp-nav-btn
       {:type "button" :aria-label "Next month"
        :on-click #(swap! !view shift-month 1)}
       "›"]]
     [:div.dp-weekdays
      (for [[idx initial] (map-indexed vector weekday-initials)]
        ^{:key idx} [:div.dp-weekday initial])]
     [:div.dp-grid
      (for [[idx {:keys [day adjacent?]}] (map-indexed vector (month-cells view))]
        (if adjacent?
          ^{:key idx} [:div.dp-day.adjacent day]
          (let [cell-date (assoc view :day day)]
            ^{:key idx}
            [:button.dp-day
             {:type "button"
              :class (cond
                       (same-date? selected cell-date) "selected"
                       (same-date? today cell-date) "today")
              :on-click #(on-change (assoc value :date cell-date))}
             day])))]]))

(defn- clock-field
  "One of the clock's digit boxes. Typing is held as a draft so a half-typed
   number doesn't jump under the cursor; anything in range commits as it is
   typed, the rest is clamped on the way out."
  [{:keys [value lowest highest pad? label on-commit]}]
  (r/with-let [!draft (r/atom nil)]
    (let [digits-of (fn [e] (str/replace (.. e -target -value) #"\D" ""))]
      [:input.dp-clock-field
       {:type "text" :inputMode "numeric" :maxLength 2
        :aria-label label
        :value (or @!draft (if pad? (.padStart (str value) 2 "0") (str value)))
        :on-focus #(.. % -target select)
        :on-change (fn [e]
                     (let [digits (digits-of e)]
                       (reset! !draft digits)
                       (when (seq digits)
                         (let [typed (js/parseInt digits 10)]
                           (when (<= lowest typed highest)
                             (on-commit typed))))))
        :on-blur (fn []
                   (when-let [digits @!draft]
                     (let [typed (if (seq digits) (js/parseInt digits 10) value)]
                       (on-commit (max lowest (min highest typed)))))
                   (reset! !draft nil))
        :on-key-down (fn [e]
                       (when (= "Enter" (.-key e))
                         (.preventDefault e)
                         (.. e -target blur)))}])))

(defn- time-box [value on-change]
  (let [{:keys [hour minute pm?]} (clock-parts (or (:time value) default-time-minutes))
        set-clock! (fn [parts]
                     (on-change (assoc value :time (clock-parts->minutes parts))))]
    [:div.dp-time
     [:div.dp-time-label "Time"]
     [:div.dp-time-box
      [datetime/clock-icon]
      [:div.dp-clock
       [clock-field {:value hour :lowest 1 :highest 12 :label "Hour"
                     :on-commit #(set-clock! {:hour % :minute minute :pm? pm?})}]
       ":"
       [clock-field {:value minute :lowest 0 :highest 59 :pad? true :label "Minute"
                     :on-commit #(set-clock! {:hour hour :minute % :pm? pm?})}]]
      [:div.dp-ampm
       [:button.dp-ampm-opt
        {:type "button"
         :class (when-not pm? "active")
         :on-click #(set-clock! {:hour hour :minute minute :pm? false})}
        "AM"]
       [:button.dp-ampm-opt
        {:type "button"
         :class (when pm? "active")
         :on-click #(set-clock! {:hour hour :minute minute :pm? true})}
        "PM"]]]]))

;; ---------------------------------------------------------------------------
;; Public component
;; ---------------------------------------------------------------------------

(defn date-picker
  "Calendar + optional time picker. `value` is the local selection map (see ns
   docstring); `on-change` receives the next value map. The mode toggle, quick
   chips and calendar can each be hidden so a caller can drive date/time itself.
   The time picker shows whenever `value` is timed."
  [{:keys [value on-change show-mode-toggle? show-quick-chips? show-calendar? show-time?]
    :or {show-mode-toggle? true show-quick-chips? true show-calendar? true}}]
  (r/with-let [!view (r/atom (or (:date value) (today-date)))]
    [frame
     (when show-mode-toggle? [mode-toggle value on-change])
     (when show-quick-chips? [quick-chips value on-change !view])
     (when show-calendar? [calendar value on-change !view])
     (when (if (some? show-time?) show-time? (timed? value))
       [time-box value on-change])]))
